import React from 'react';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { formatDistanceToNow, formatDistanceToNowStrict } from 'date-fns';
import { AlarmClock, CheckCircle2, History as HistoryIcon, MoreHorizontal, Pause, Pencil, Play, Plus, Power, Timer, Trash2, Triangle, XCircle, } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { KpiStrip } from '@/components/kpi-strip';
import { SegmentedToggle } from '@/components/segmented-toggle';
import { isInternalAdmin } from '@/lib/roles';
import { useAuthenticationState } from '@/hooks/use-authentication';
import { cn, errorHandler } from '@/lib/utils';
import { createSchedule, deleteSchedule, fetchCronJobs, fetchScheduleRuns, fetchSchedules, runScheduleNow, updateSchedule, } from '@/services/internal-cron';
export const Route = createFileRoute('/console/_authenticated/_layout/internal/_layout/cron/')({
    component: CronPage,
});
const EMPTY_FORM = {
    id: null,
    name: '',
    command: '',
    cronExpression: '',
    arguments: '',
    priority: 0,
    disabled: false,
};
function classifySchedule(s) {
    if (s.disabled)
        return 'disabled';
    if (s.lastRun?.status === 'failed')
        return 'failed';
    return 'healthy';
}
function isFailedWithin24h(run) {
    if (run === null || run.status !== 'failed')
        return false;
    const started = new Date(run.startedAt).getTime();
    return Date.now() - started < 24 * 60 * 60 * 1000;
}
function relative(value, future) {
    if (value === null || value === undefined)
        return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime()))
        return '—';
    if (future) {
        return `in ${formatDistanceToNowStrict(d)}`;
    }
    return `${formatDistanceToNow(d)} ago`;
}
function CronPage() {
    const { user } = useAuthenticationState();
    const queryClient = useQueryClient();
    const [form, setForm] = React.useState(null);
    const [historyFor, setHistoryFor] = React.useState(null);
    const [deleteTarget, setDeleteTarget] = React.useState(null);
    const [filter, setFilter] = React.useState('all');
    const { data: jobs = [] } = useQuery({
        queryKey: ['cron-jobs'],
        queryFn: fetchCronJobs,
        staleTime: 5 * 60_000,
    });
    const { data: schedules = [], isLoading } = useQuery({
        queryKey: ['cron-schedules'],
        queryFn: fetchSchedules,
        refetchInterval: 15_000,
    });
    const invalidate = () => queryClient.invalidateQueries({ queryKey: ['cron-schedules'] }).then(() => undefined);
    const createMutation = useMutation({
        mutationFn: createSchedule,
        onSuccess: async () => {
            await invalidate();
            toast.success('Schedule created');
            setForm(null);
        },
        onError: errorHandler,
    });
    const updateMutation = useMutation({
        mutationFn: updateSchedule,
        onSuccess: async () => {
            await invalidate();
            toast.success('Schedule updated');
            setForm(null);
        },
        onError: errorHandler,
    });
    const deleteMutation = useMutation({
        mutationFn: deleteSchedule,
        onSuccess: async () => {
            await invalidate();
            toast.success('Schedule deleted');
            setDeleteTarget(null);
        },
        onError: errorHandler,
    });
    const runMutation = useMutation({
        mutationFn: runScheduleNow,
        onSuccess: async (data) => {
            await invalidate();
            const ok = data.exitCode === 0;
            toast[ok ? 'success' : 'error'](ok ? 'Command finished successfully' : `Command exited with code ${data.exitCode}`);
        },
        onError: errorHandler,
    });
    // KPIs derived from current schedules — recomputed every refetch.
    const kpis = React.useMemo(() => {
        const enabled = schedules.filter((s) => !s.disabled).length;
        const failed24 = schedules.filter((s) => isFailedWithin24h(s.lastRun)).length;
        const upcoming = schedules
            .filter((s) => !s.disabled && s.nextRunDate !== null && s.nextRunDate !== undefined)
            .map((s) => ({ schedule: s, when: new Date(s.nextRunDate) }))
            .filter(({ when }) => !Number.isNaN(when.getTime()))
            .sort((a, b) => a.when.getTime() - b.when.getTime());
        const next = upcoming[0];
        return [
            {
                label: 'Total schedules',
                value: schedules.length,
                hint: schedules.length === 1 ? '1 command' : `${schedules.length} commands`,
                icon: <AlarmClock className="h-5 w-5"/>,
            },
            {
                label: 'Enabled',
                value: enabled,
                hint: enabled === schedules.length ? 'all running' : `${schedules.length - enabled} disabled`,
                icon: <Power className="h-5 w-5"/>,
            },
            {
                label: 'Failed (24h)',
                value: failed24,
                hint: failed24 === 0 ? 'all clear' : 'needs attention',
                icon: (<Triangle className={cn('h-5 w-5', failed24 > 0 ? 'fill-destructive/15 text-destructive' : '')}/>),
            },
            {
                label: 'Next run',
                value: next ? relative(next.when.toISOString(), true) : '—',
                hint: next ? next.schedule.name : 'no upcoming runs',
                icon: <Timer className="h-5 w-5"/>,
            },
        ];
    }, [schedules]);
    const counts = React.useMemo(() => {
        const out = { all: schedules.length, healthy: 0, failed: 0, disabled: 0 };
        for (const s of schedules) {
            out[classifySchedule(s)]++;
        }
        return out;
    }, [schedules]);
    const visible = React.useMemo(() => {
        if (filter === 'all')
            return schedules;
        return schedules.filter((s) => classifySchedule(s) === filter);
    }, [schedules, filter]);
    if (!isInternalAdmin(user)) {
        return <Navigate to="/console/internal"/>;
    }
    const submitForm = () => {
        if (form === null)
            return;
        const data = {
            command: form.command,
            name: form.name,
            cronExpression: form.cronExpression,
            arguments: form.arguments,
            priority: form.priority,
            disabled: form.disabled,
        };
        if (form.id !== null) {
            updateMutation.mutate({ id: form.id, data });
        }
        else {
            createMutation.mutate(data);
        }
    };
    return (<div className="flex flex-col gap-6 p-6 max-w-6xl mx-auto w-full">
            <header className="flex items-end justify-between gap-4 border-b pb-4">
                <div className="space-y-1">
                    <h1 className="text-3xl font-semibold flex items-center gap-2">
                        <AlarmClock className="h-7 w-7"/>
                        Cron
                    </h1>
                    <p className="text-sm text-muted-foreground max-w-prose">
                        Schedule and observe internal console commands. One master cron
                        (<code className="text-xs">scheduler:execute</code>) dispatches everything below.
                    </p>
                </div>
                <Button onClick={() => setForm(EMPTY_FORM)}>
                    <Plus className="h-4 w-4"/>
                    New schedule
                </Button>
            </header>

            <KpiStrip items={kpis}/>

            <div className="flex items-center justify-between gap-2 flex-wrap">
                <SegmentedToggle value={filter} onValueChange={setFilter} layoutId="cron-filter" items={[
            { value: 'all', label: <FilterLabel text="All" count={counts.all}/> },
            { value: 'healthy', label: <FilterLabel text="Healthy" count={counts.healthy}/> },
            { value: 'failed', label: <FilterLabel text="Failed" count={counts.failed}/> },
            { value: 'disabled', label: <FilterLabel text="Disabled" count={counts.disabled}/> },
        ]}/>
                <p className="text-xs text-muted-foreground">
                    Polls every 15s · click <strong>Run now</strong> to fire in-process
                </p>
            </div>

            {isLoading ? (<Card>
                    <CardContent className="p-10 text-sm text-muted-foreground text-center">Loading…</CardContent>
                </Card>) : visible.length === 0 ? (<Card>
                    <CardContent className="p-10 text-sm text-muted-foreground text-center space-y-2">
                        <p>
                            {filter === 'all'
                ? 'No schedules yet.'
                : `No ${filter} schedules.`}
                        </p>
                        {filter === 'all' && (<Button variant="outline" onClick={() => setForm(EMPTY_FORM)}>
                                <Plus className="h-4 w-4"/>
                                Add the first schedule
                            </Button>)}
                    </CardContent>
                </Card>) : (<Card>
                    <CardContent className="p-0 divide-y">
                        {visible.map((s) => (<ScheduleListItem key={s.id} schedule={s} running={runMutation.isPending && runMutation.variables === s.id} onRun={() => runMutation.mutate(s.id)} onEdit={() => setForm({
                    id: s.id,
                    name: s.name,
                    command: s.command,
                    cronExpression: s.cronExpression,
                    arguments: s.arguments ?? '',
                    priority: s.priority,
                    disabled: s.disabled,
                })} onToggleDisabled={() => updateMutation.mutate({
                    id: s.id,
                    data: { disabled: !s.disabled },
                })} onHistory={() => setHistoryFor(s)} onDelete={() => setDeleteTarget(s)}/>))}
                    </CardContent>
                </Card>)}

            <ScheduleFormDialog open={form !== null} form={form ?? EMPTY_FORM} jobs={jobs} pending={createMutation.isPending || updateMutation.isPending} onChange={setForm} onClose={() => setForm(null)} onSubmit={submitForm}/>

            <HistoryDrawer schedule={historyFor} onClose={() => setHistoryFor(null)}/>

            <ConfirmDeleteDialog target={deleteTarget} pending={deleteMutation.isPending} onCancel={() => setDeleteTarget(null)} onConfirm={(s) => deleteMutation.mutate(s.id)}/>
        </div>);
}
function FilterLabel({ text, count }) {
    return (<span className="inline-flex items-center gap-1.5">
            {text}
            <span className="tabular-nums text-xs opacity-80">{count}</span>
        </span>);
}
function ScheduleListItem({ schedule, running, onRun, onEdit, onToggleDisabled, onHistory, onDelete, }) {
    const status = schedule.lastRun?.status ?? null;
    const dotClass = schedule.disabled
        ? 'bg-muted-foreground/40'
        : status === 'failed'
            ? 'bg-destructive'
            : status === 'success'
                ? 'bg-emerald-500'
                : status === 'running'
                    ? 'bg-amber-500 animate-pulse'
                    : 'bg-muted-foreground/40';
    return (<div className={cn('flex items-center gap-4 p-4', schedule.disabled && 'opacity-60')}>
            {/* Status dot */}
            <span className={cn('h-2.5 w-2.5 rounded-full shrink-0', dotClass)} aria-hidden="true"/>

            {/* Main info */}
            <div className="min-w-0 flex-1 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-1">
                <div className="min-w-0 md:col-span-1">
                    <div className="font-medium truncate">{schedule.name}</div>
                    <div className="font-mono text-xs text-muted-foreground truncate">{schedule.command}</div>
                </div>

                <div className="min-w-0">
                    <div className="font-mono text-xs">{schedule.cronExpression}</div>
                    <div className="text-xs text-muted-foreground capitalize truncate">
                        {schedule.cronExpressionTranslated}
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    <LastRunChip run={schedule.lastRun ?? null}/>
                    <NextRunChip nextRunDate={schedule.nextRunDate ?? null} disabled={schedule.disabled}/>
                </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1.5 shrink-0">
                <Button size="sm" variant="default" onClick={onRun} disabled={running || schedule.disabled} className="gap-1.5">
                    <Play className="h-3.5 w-3.5"/>
                    {running ? 'Running…' : 'Run now'}
                </Button>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" aria-label="More actions">
                            <MoreHorizontal className="h-4 w-4"/>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={onHistory}>
                            <HistoryIcon className="h-4 w-4"/>
                            History
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={onEdit}>
                            <Pencil className="h-4 w-4"/>
                            Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={onToggleDisabled}>
                            {schedule.disabled ? (<>
                                    <Play className="h-4 w-4"/>
                                    Enable
                                </>) : (<>
                                    <Pause className="h-4 w-4"/>
                                    Disable
                                </>)}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
                            <Trash2 className="h-4 w-4"/>
                            Delete
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>);
}
function LastRunChip({ run }) {
    if (run === null) {
        return <span className="text-muted-foreground">never run</span>;
    }
    const Icon = run.status === 'success' ? CheckCircle2 : run.status === 'failed' ? XCircle : Timer;
    const color = run.status === 'success'
        ? 'text-emerald-600 dark:text-emerald-400'
        : run.status === 'failed'
            ? 'text-destructive'
            : 'text-amber-600 dark:text-amber-400';
    return (<span className={cn('inline-flex items-center gap-1', color)}>
            <Icon className="h-3.5 w-3.5"/>
            <span className="capitalize">{run.status}</span>
            <span className="text-muted-foreground">· {relative(run.startedAt, false)}</span>
        </span>);
}
function NextRunChip({ nextRunDate, disabled, }) {
    if (disabled) {
        return <span className="text-muted-foreground">paused</span>;
    }
    if (nextRunDate === null) {
        return <span className="text-muted-foreground">—</span>;
    }
    return (<span className="inline-flex items-center gap-1 text-muted-foreground">
            <Timer className="h-3.5 w-3.5"/>
            next {relative(nextRunDate, true)}
        </span>);
}
function ScheduleFormDialog({ open, form, jobs, pending, onChange, onClose, onSubmit, }) {
    const isEdit = form.id !== null;
    return (<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{isEdit ? 'Edit schedule' : 'New schedule'}</DialogTitle>
                    <DialogDescription>
                        Commands are restricted to the internal allowlist for safety.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    <div className="space-y-2">
                        <Label>Command</Label>
                        <Select value={form.command} onValueChange={(value) => {
            const job = jobs.find((j) => j.command === value);
            onChange({
                ...form,
                command: value,
                cronExpression: form.cronExpression === '' && job
                    ? job.suggestedCron
                    : form.cronExpression,
                name: form.name === '' && job ? job.label : form.name,
            });
        }} disabled={isEdit}>
                            <SelectTrigger>
                                <SelectValue placeholder="Pick a command"/>
                            </SelectTrigger>
                            <SelectContent>
                                {jobs.map((j) => (<SelectItem key={j.command} value={j.command}>
                                        {j.label}
                                    </SelectItem>))}
                            </SelectContent>
                        </Select>
                        {form.command !== '' && (<p className="text-xs text-muted-foreground">
                                {jobs.find((j) => j.command === form.command)?.description}
                            </p>)}
                    </div>

                    <div className="space-y-2">
                        <Label>Name</Label>
                        <Input value={form.name} onChange={(e) => onChange({ ...form, name: e.target.value })} placeholder="A descriptive label"/>
                    </div>

                    <div className="space-y-2">
                        <Label>Cron expression</Label>
                        <Input value={form.cronExpression} onChange={(e) => onChange({ ...form, cronExpression: e.target.value })} placeholder="0 2 * * *" className="font-mono"/>
                    </div>

                    <div className="space-y-2">
                        <Label>Arguments (optional)</Label>
                        <Textarea value={form.arguments} onChange={(e) => onChange({ ...form, arguments: e.target.value })} placeholder="--option=value" rows={2} className="font-mono"/>
                    </div>

                    <div className="flex items-center justify-between">
                        <Label htmlFor="disabled">Disabled</Label>
                        <Switch id="disabled" checked={form.disabled} onCheckedChange={(checked) => onChange({ ...form, disabled: checked })}/>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={pending}>
                        Cancel
                    </Button>
                    <Button onClick={onSubmit} disabled={pending}>
                        {isEdit ? 'Save' : 'Create'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>);
}
function HistoryDrawer({ schedule, onClose }) {
    const command = schedule?.command ?? '';
    const { data: runs = [], isLoading } = useQuery({
        queryKey: ['cron-runs', command],
        queryFn: () => fetchScheduleRuns(command),
        enabled: command !== '',
    });
    return (<Sheet open={schedule !== null} onOpenChange={(o) => !o && onClose()}>
            <SheetContent side="right" className="sm:max-w-2xl overflow-y-auto">
                <SheetHeader>
                    <SheetTitle>History — {schedule?.name}</SheetTitle>
                    <SheetDescription className="font-mono text-xs">
                        {schedule?.command}
                    </SheetDescription>
                </SheetHeader>

                <div className="flex flex-col gap-3 mt-4">
                    {isLoading ? (<p className="text-sm text-muted-foreground">Loading…</p>) : runs.length === 0 ? (<p className="text-sm text-muted-foreground">No runs recorded yet.</p>) : (runs.map((r) => <RunCard key={r.publicId} run={r}/>))}
                </div>
            </SheetContent>
        </Sheet>);
}
function RunCard({ run }) {
    const Icon = run.status === 'success' ? CheckCircle2 : run.status === 'failed' ? XCircle : Timer;
    const color = run.status === 'success'
        ? 'text-emerald-600 dark:text-emerald-400'
        : run.status === 'failed'
            ? 'text-destructive'
            : 'text-amber-600 dark:text-amber-400';
    return (<Card>
            <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between gap-3">
                    <span className={cn('inline-flex items-center gap-1.5 text-sm font-medium', color)}>
                        <Icon className="h-4 w-4"/>
                        <span className="capitalize">{run.status}</span>
                    </span>
                    <span className="text-xs text-muted-foreground">
                        {relative(run.startedAt, false)}
                    </span>
                </div>
                <div className="text-xs text-muted-foreground">
                    Exit code: <span className="font-mono">{run.exitCode ?? '—'}</span>
                    {run.triggeredByEmail !== null && (<>
                            {' · triggered by '}
                            <span className="font-mono">{run.triggeredByEmail}</span>
                        </>)}
                </div>
                {run.outputTail !== null && run.outputTail !== '' && (<pre className="bg-muted/50 rounded p-3 text-xs whitespace-pre-wrap overflow-x-auto max-h-64">
                        {run.outputTail}
                    </pre>)}
            </CardContent>
        </Card>);
}
function ConfirmDeleteDialog({ target, pending, onCancel, onConfirm, }) {
    return (<Dialog open={target !== null} onOpenChange={(o) => !o && onCancel()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Delete schedule?</DialogTitle>
                    <DialogDescription>
                        This removes the schedule entry only. Past audit runs in
                        <code className="text-xs"> adora_internal_cron_runs </code>
                        are preserved.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="outline" onClick={onCancel} disabled={pending}>
                        Cancel
                    </Button>
                    <Button variant="destructive" onClick={() => target !== null && onConfirm(target)} disabled={pending}>
                        Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>);
}
